import { difference } from 'lodash';
import { useState } from 'react';

import type { TreeItem } from '@revelio/iso-utility';

import {
  CardListSelectController,
  type ColumnItem,
  type ColumnSet,
} from '../../../../../shared/ui/card-list';
import type { ReportBuilderDeliverable } from '../../report-builder.model';
import {
  selectReport,
  useReportBuilderSelector,
} from '../../report-builder.repository';
import { updateReportEntitySelections } from './report-entity-selection.utils';
import { ReportEntitySelectionsLegacyModal } from './report-entity-selections/report-entity-selections-legacy-modal';
import { shouldUseLegacyReportSelectionsModal } from './report-entity-selections/report-entity-selections-legacy-modal-helpers';
import { ReportEntitySelectionsModal } from './report-entity-selections/report-entity-selections-modal';
import { isHiddenColumn } from './report-hidden-columns';
import { useReportsRoleCustomTaxonomy } from './useReportsRoleCustomTaxonomy';
import { useReportsSkillTaxonomy } from './useReportsSkillTaxonomy';
import {
  getColumnItemFromReportEntityConfigColumn,
  type IReportEntityMap,
  type ReportEntityConfig,
} from './utils';

const generateEntitySelectionOptions = (
  report: ReportBuilderDeliverable,
  reportEntityMap: IReportEntityMap
): ColumnSet<string>[] => {
  // @ts-expect-error noUncheckedIndexedAccess
  const entityConfigs: ReportEntityConfig = reportEntityMap[report.reportType];
  const entitySelections = report?.selections;
  const columns = entityConfigs.columns
    // Exclude hidden custom taxonomy entities from UI
    .filter((columnItem) => !isHiddenColumn(columnItem))
    .map((columnItem) => {
      const selectedValues = entitySelections[columnItem.id]
        ? // @ts-expect-error noUncheckedIndexedAccess
          Object.values(entitySelections[columnItem.id])
            .map((value) => value.item?.shortName)
            .join(', ')
        : '';

      const columnItemProps: ColumnItem<string> =
        getColumnItemFromReportEntityConfigColumn(columnItem);
      return {
        ...columnItemProps,
        detail: selectedValues || 'None Selected',
      };
    });
  const entitySelectionOptions = [
    {
      ...entityConfigs,
      columns,
    },
  ];

  return entitySelectionOptions;
};

const EditEntityValues = ({
  selectedReportId,
  reportEntityMap,
}: {
  selectedReportId: ReportBuilderDeliverable['id'];
  reportEntityMap: IReportEntityMap;
}) => {
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);

  const report = useReportBuilderSelector(selectReport(selectedReportId));

  const entitySelections = report?.selections;

  const entityConfigs = report?.reportType
    ? (reportEntityMap[report?.reportType] ?? null)
    : null;
  useReportsRoleCustomTaxonomy(
    entityConfigs,
    selectedReportId,
    entitySelections
  );

  // Inject hidden skill taxonomy fields if requested by the report config
  useReportsSkillTaxonomy(
    entityConfigs ?? null,
    selectedReportId,
    entitySelections
  );

  const currentEntityConfig = entityConfigs?.columns?.find(
    (config) => config.id === selectedEntity
  );

  // Filter out any pre-selected entities that we are intentionally hiding from the summary
  const values = entitySelections
    ? Object.keys(entitySelections).filter((id) => !isHiddenColumn({ id }))
    : [];

  const handleSelectionChange = (value: string[]) => {
    const currentSelection =
      value.length > values.length
        ? value[value.length - 1]
        : difference(values, value)[0];

    if (currentSelection) {
      setSelectedEntity(currentSelection);
    }
  };

  const closeModal = () => {
    setSelectedEntity(null);
  };

  const submitSelections = (selections: Record<string, TreeItem<string>>) => {
    if (
      selectedEntity &&
      selectedReportId !== null &&
      selectedReportId !== undefined
    ) {
      updateReportEntitySelections({
        reportId: selectedReportId,
        reportEntity: selectedEntity,
        selections,
      });
    }

    closeModal();
  };

  const { filters: selectedSelectionLists, limit: selectedListLimit } =
    currentEntityConfig?.reportEntityOptions?.selectionList || {};

  const entitySelectionOptions = report
    ? generateEntitySelectionOptions(report, reportEntityMap)
    : [];

  const onCloseModal = () => setSelectedEntity(null);

  const initialSelections = selectedEntity
    ? entitySelections?.[selectedEntity]
    : {};

  const useNewModal = !shouldUseLegacyReportSelectionsModal(
    selectedSelectionLists ?? []
  );

  return (
    <>
      <CardListSelectController<string>
        data-testid="edit-report-builder-entity-values"
        items={entitySelectionOptions}
        value={values}
        onChange={handleSelectionChange}
        variant="report"
        gridProps={{
          spacing: '10px',
          columns: 2,
          gridAutoFlow: 'row',
        }}
      />

      {currentEntityConfig && (
        <>
          <ReportEntitySelectionsModal
            isOpen={useNewModal && !!selectedEntity}
            onClose={onCloseModal}
            header={currentEntityConfig.label ?? ''}
            selectionLists={selectedSelectionLists ?? []}
            initialSelections={initialSelections}
            limit={selectedListLimit}
            onSubmit={submitSelections}
          />
          <ReportEntitySelectionsLegacyModal
            headerLabel={currentEntityConfig.label}
            isOpen={!useNewModal && !!selectedEntity}
            onClose={onCloseModal}
            submitHandler={submitSelections}
            initialSelections={initialSelections}
            selectionLists={selectedSelectionLists}
            limit={selectedListLimit}
          />
        </>
      )}
    </>
  );
};

export default EditEntityValues;
